import * as Yup from 'yup';
import { Field, FieldProps } from 'formik';
import { FormControlLabel, Switch } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { Locale } from '@synergy/models';
import { PaloField, PaloFormGrid } from '@synergy/components-palo';
import { PaloThemeProvider } from '@synergy/theme-palo';
import { ReactNode } from 'react';
import { defaultLocale } from '@synergy/config';
import { errorsToTouch, fields } from '@synergy/utils';

import { DemandEndField, DemandStartField } from '../pages/demand-detail-overview-section-date';
import { DemandSteps } from '../models/demand-steps';
import { FormikGroupItem } from '../../../../../common/components/new-stepper/formik-group-item';
import { OverviewFormValues } from '../models/overview-form-values';
import { PaloFormikField } from '../../../../../common/components/palo/palo-formik-field';
import { PaloRemarkPair } from '../../../../../common/components/palo/palo-remark-pair';
import { PaloTextControl } from '../../../../../common/components/palo/palo-text-control';
import {
    PartialContact,
    PoolContactAutocompleteList,
} from '../../../../../common/components/autocomplete-list/contact-autocomplete-list/pool-contact-autocomplete-list';
import {
    demandDetailOverviewBaselineSelector,
    demandDetailOverviewDataSelector,
} from '../store/overview/demand-detail-overview-selectors';
import { demandDetailStepperStateSelector } from '../store/demand-detail-selectors';
import { selectedLanguageSelector } from '../../../../../common/selectors/selected-language-selectors';
import { submitOverview } from '../store/overview/demand-detail-overview-actions';
import { useAppSelector } from '../../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../../store/use-thunk-dispatch';

const fieldNames = fields<OverviewFormValues>();

function validationSchema(selectedLanguage: Locale) {
    return Yup.object().shape({
        [fieldNames.title]: Yup.object().shape({
            [selectedLanguage]: Yup.string(),
            [defaultLocale]: Yup.string().required('app.error.required'),
        }),
        [fieldNames.startDate]: Yup.date().required('app.error.required').typeError('app.error.required'),
        [fieldNames.endDate]: Yup.date()
            .min(Yup.ref('startDate'), 'app.error.minStartDate')
            .required('app.error.required')
            .typeError('app.error.required'),
        [fieldNames.responsibleContact]: Yup.mixed().required('app.error.required').typeError('app.error.required'),
    });
}

export interface PaloDemandDetailOverviewProps {
    /** The positions, drawn between the head and the remarks. */
    positions?: ReactNode;
}

/**
 * Title, person in charge and reminder; start and end; the description — three
 * rows. The positions follow on the same page (#2253).
 */
const DEMAND_HEAD_AREAS = [
    'title title responsibleContact reminder',
    'start start end end',
    'description description description description',
];

export const PaloDemandDetailOverview = ({ positions }: PaloDemandDetailOverviewProps): JSX.Element => {
    const dispatch = useThunkDispatch();
    const overviewData = useAppSelector(demandDetailOverviewDataSelector);
    // The step as the server has it — see FormikGroupItem.dirtyBaseline.
    const baseline = useAppSelector(demandDetailOverviewBaselineSelector);
    const contacts = useAppSelector(state => state.contacts.sortedByName);
    const selectedLanguage: Locale = useAppSelector(selectedLanguageSelector);
    const readonly = useAppSelector(demandDetailStepperStateSelector).readonlySteps.includes(DemandSteps.Overview);

    return (
        <FormikGroupItem
            groupItemId={DemandSteps.Overview}
            dirtyBaseline={baseline}
            initialValues={overviewData.values}
            onSubmit={data => dispatch(submitOverview(data))}
            validationSchema={validationSchema(selectedLanguage)}
            initialErrors={overviewData.errors}
            initialTouched={errorsToTouch(overviewData.errors)}
            enableReinitialize={true}
            isInitialValid={overviewData.isValid}
        >
            <PaloThemeProvider>
                <PaloFormGrid columns={4} labels="side" areas={DEMAND_HEAD_AREAS}>
                    <PaloTextControl
                        name={`${fieldNames.title}.${selectedLanguage}`}
                        label={<FormattedMessage id="app.label.title" />}
                        required={selectedLanguage === defaultLocale}
                        area="title"
                        autoFocus
                        disabled={readonly}
                    />
                    <PaloFormikField<PartialContact>
                        name={fieldNames.responsibleContact}
                        label={<FormattedMessage id="app.common.responsibleContact" />}
                        required
                        area="responsibleContact"
                    >
                        {({ field, hasError, setValue }) => (
                            <PoolContactAutocompleteList
                                items={contacts}
                                label={<></>}
                                onSelected={setValue}
                                required={false}
                                error={hasError}
                                readOnly={readonly}
                                {...field}
                            />
                        )}
                    </PaloFormikField>
                    {/* A property of the survey like the others, not a switch in
                        the title of a section (#2253). */}
                    <PaloField
                        label={<FormattedMessage id="app.demands.detail.overview.remindCustomer" />}
                        name={fieldNames.enableExpirationNotifications}
                        area="reminder"
                    >
                        <Field name={fieldNames.enableExpirationNotifications}>
                            {({ field, form }: FieldProps<boolean>) => (
                                <FormControlLabel
                                    label={
                                        <FormattedMessage
                                            id="app.demands.detail.overview.remindCustomerDays"
                                            values={{ value: 3 }}
                                        />
                                    }
                                    sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '12.5px' } }}
                                    control={
                                        <Switch
                                            name={field.name}
                                            checked={field.value}
                                            onChange={() => form.setFieldValue(field.name, !field.value)}
                                            color="primary"
                                            size="small"
                                            disabled={readonly}
                                        />
                                    }
                                />
                            )}
                        </Field>
                    </PaloField>
                    <DemandStartField />
                    <DemandEndField />
                    <PaloTextControl
                        name={`${fieldNames.description}.${selectedLanguage}`}
                        label={<FormattedMessage id="app.label.description" />}
                        multiline
                        minRows={2}
                        maxRows={12}
                        area="description"
                        disabled={readonly}
                    />
                </PaloFormGrid>
                {positions}
                <PaloRemarkPair
                    external={{
                        name: `${fieldNames.noteToCustomer}.${selectedLanguage}`,
                        label: <FormattedMessage id="app.header.noteToCustomer" />,
                    }}
                    internalName={fieldNames.internalNote}
                    readonly={readonly}
                />
            </PaloThemeProvider>
        </FormikGroupItem>
    );
};
